import Dialog from "../../General/ConfirmableDialog";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Chip,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import useAuth from "../../../hooks/useAuth";
import {
  createPreTripInspectionCheck,
  updatePreTripInspectionCheck,
} from "../../../api/preTripInspectionCheckService";
import { PreTripInspectionCheck } from "../../../interfaces/Safety/IPreTripInspectionCheck";

interface Props {
  open: boolean;
  onClose: () => void;
  driverId: string;
  driverName: string;
  dates: Dayjs[];
  existing: (PreTripInspectionCheck | null)[];
  onSaved: (check: PreTripInspectionCheck | PreTripInspectionCheck[]) => void;
}

const fields = [
  { name: "allGood", label: "All Good" },
  { name: "noPreTripInspectionMade", label: "No PTI" },
  { name: "needAttention", label: "Need Attention" },
  { name: "isManagement", label: "Management" },
  { name: "hasTechnicalIssues", label: "Technical Issues" },
  { name: "offDuty", label: "Off Duty" },
] as const;

type FlagName = (typeof fields)[number]["name"];

const createFlagState = (): Record<FlagName, boolean> => ({
  allGood: false,
  noPreTripInspectionMade: false,
  needAttention: false,
  isManagement: false,
  hasTechnicalIssues: false,
  offDuty: false,
});

const PreTripInspectionCheckDialog = ({
  open,
  onClose,
  driverId,
  driverName,
  dates,
  existing,
  onSaved,
}: Props) => {
  const { token } = useAuth();
  const [flags, setFlags] =
    useState<Record<FlagName, boolean>>(createFlagState);
  const [indeterminateFlags, setIndeterminateFlags] =
    useState<Record<FlagName, boolean>>(createFlagState);
  const [dirtyFlags, setDirtyFlags] =
    useState<Record<FlagName, boolean>>(createFlagState);
  const [note, setNote] = useState("");
  const [noteIndeterminate, setNoteIndeterminate] = useState(false);
  const [noteDirty, setNoteDirty] = useState(false);

  useEffect(() => {
    const nextFlags = createFlagState();
    const nextIndeterminate = createFlagState();

    if (existing.length > 0) {
      fields.forEach((field) => {
        const values = existing.map((item) => !!item?.[field.name]);
        const allTrue = values.length > 0 && values.every(Boolean);
        const allFalse = values.every((value) => !value);

        nextFlags[field.name] = allTrue;
        nextIndeterminate[field.name] =
          !allTrue && !allFalse && values.length > 0;
      });
    }

    const noteValues = existing.map((item) => item?.notes?.trim() ?? "");
    const uniqueNotes = new Set(noteValues);
    const hasSingleNote = uniqueNotes.size === 1;
    const initialNote = hasSingleNote ? noteValues[0] : "";

    setFlags(nextFlags);
    setIndeterminateFlags(nextIndeterminate);
    setDirtyFlags(createFlagState());
    setNote(initialNote);
    setNoteIndeterminate(!hasSingleNote && existing.length > 0);
    setNoteDirty(false);
  }, [existing, open]);

  const handleChange =
    (field: FlagName) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setFlags((prev) => ({ ...prev, [field]: checked }));
      setIndeterminateFlags((prev) => ({ ...prev, [field]: false }));
      setDirtyFlags((prev) => ({ ...prev, [field]: true }));
    };

  const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
    setNoteIndeterminate(false);
    setNoteDirty(true);
  };

  const handleSave = async () => {
    if (dates.length === 0) {
      onClose();
      return;
    }

    try {
      const results = await Promise.all(
        dates.map(async (date, index) => {
          const currentExisting = existing[index] || null;
          const flagPayload: Partial<PreTripInspectionCheck> = {};

          fields.forEach((field) => {
            if (
              currentExisting &&
              !dirtyFlags[field.name] &&
              indeterminateFlags[field.name]
            ) {
              flagPayload[field.name] = !!currentExisting[field.name];
            } else {
              flagPayload[field.name] = flags[field.name];
            }
          });

          let noteValue = note.trim();
          if (
            currentExisting &&
            !noteDirty &&
            noteIndeterminate &&
            currentExisting.notes
          ) {
            noteValue = currentExisting.notes.trim();
          }

          const payload: Partial<PreTripInspectionCheck> = {
            date: date.startOf("day").format("YYYY-MM-DD"),
            driverId,
            ...flagPayload,
            notes: noteValue ? noteValue : null,
          };

          if (currentExisting) {
            return updatePreTripInspectionCheck(token, currentExisting.id, {
              ...payload,
              id: currentExisting.id,
            });
          }

          return createPreTripInspectionCheck(token, payload);
        })
      );

      onSaved(results.length === 1 ? results[0] : results);
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const isMulti = dates.length > 1;
  const formattedDates = useMemo(
    () => dates.map((date) => date.format("MMM D, YYYY")),
    [dates]
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Pre-Trip Inspection Check</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" color="text.primary">
              {driverName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isMulti
                ? `${formattedDates.length} days selected`
                : formattedDates[0] || ""}
            </Typography>
            {isMulti && (
              <Stack
                direction="row"
                spacing={0.75}
                flexWrap="wrap"
                rowGap={0.75}
              >
                {formattedDates.map((label) => (
                  <Chip key={label} label={label} size="small" />
                ))}
              </Stack>
            )}
          </Stack>
          <Stack spacing={1.5}>
            {fields.map((f) => (
              <FormControlLabel
                key={f.name}
                control={
                  <Checkbox
                    checked={flags[f.name] || false}
                    indeterminate={
                      indeterminateFlags[f.name] && !dirtyFlags[f.name]
                    }
                    onChange={handleChange(f.name)}
                  />
                }
                label={f.label}
              />
            ))}
            <TextField
              label="Notes"
              value={note}
              onChange={handleNoteChange}
              multiline
              minRows={3}
              fullWidth
              placeholder={
                noteIndeterminate && !noteDirty
                  ? "Multiple values"
                  : undefined
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreTripInspectionCheckDialog;
